/**
 * WebSocket progress for long-running API calls.
 * Self-contained: uses env (REACT_APP_BASE_WEBHOOK) and localStorage token only.
 *
 * ---------------------------------------------------------------------------
 * DOCS: FIREFOX / NETWORK TIMING — READ BEFORE CHANGING `requestWithProgress`
 * ---------------------------------------------------------------------------
 * See: ../docs/WEBSOCKET_PROGRESS_PARALLEL_HTTP.md
 *
 * RULE (ANTI-PATTERN WE FIXED): Never call `apiFetch` only inside
 * `ws.addEventListener("open", ...)`. That gates HTTP on the WebSocket
 * handshake; Firefox often completes that handshake later than Chrome/Safari,
 * so DevTools showed a long gap before any REST request started — not because
 * `fetch` was slow, but because we delayed it until `open`.
 *
 * RULE (CURRENT DESIGN): Open WS, register listeners, run HTTP in parallel.
 * Resolve the job from the HTTP response body; WS carries optional progress.
 * ---------------------------------------------------------------------------
 */
import { apiFetch } from "../utils/apiFetch";

// ─── WebSocket URL ─────────────────────────────────────────────────────────
// Base URL for the app progress WebSocket. Backend sends progress to the same
// user that made the HTTP request (matched by JWT from ?token= or cookie).

const BASE_WEBSOCKET_URL =
  process.env.REACT_APP_BASE_WEBHOOK ||
  `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/api/app`;

/**
 * Builds the app progress WebSocket URL with auth token for the current user.
 * Backend decodes the token to get user_id and sends progress only to that user.
 * @returns {string} WebSocket URL with token query param.
 */
export function getProgressWebSocketUrl() {
  const token = localStorage.getItem("token");
  return `${BASE_WEBSOCKET_URL}?token=${encodeURIComponent(token ?? "")}`;
}

// ─── Backend contract (for reference) ───────────────────────────────────────
// Backend: WS at REACT_APP_BASE_WEBHOOK or /api/app. Auth via ?token= or cookie;
// JWT decoded for user_id; progress sent via ws.manager.send_to_user(user_id, payload).
// Payloads: { event: "connected", user_id } on connect;
//           { event: "progress", stage, message, pct?, elapsed?, task_id? };
//           { event: "done", result?, task_id? } (result often only { total_count });
// Errors:   { stage: "error", message }.
// We resolve the promise only from the HTTP response body, not from WS "done".

// ─── requestWithProgress ──────────────────────────────────────────────────

/**
 * Run an HTTP request while listening for live progress on the app WebSocket.
 * Resolves with the HTTP response body when the request completes; progress
 * events are streamed via onProgress(pct, message).
 *
 * HTTP runs **in parallel** with the WebSocket connect — see module header and
 * `WEBSOCKET_PROGRESS_PARALLEL_HTTP.md`.
 *
 * @param {Object} request - { url: string, options: RequestInit }
 * @param {Function} onProgress - (pct: number, message: string, stage?: string) => void. Backend sends stage; use it for i18n (e.g. t(`progress_stages.${stage}`, message)).
 * @returns {Promise<object>} The JSON body of the HTTP response (when response.ok).
 * @throws {Error} On HTTP failure, message `stage === "error"`, or WebSocket `close`
 *   before the HTTP response settles (progress-only WS `error` does **not** reject).
 */
export function requestWithProgress(request = {}, onProgress = () => {}) {
  const wsUrl = getProgressWebSocketUrl();

  // ─── Promise and settlement state ─────────────────────────────────────────
  // We resolve/reject at most once. "settled" guards against double-settle
  // (e.g. WS "done" and HTTP response both firing).

  let ws = null;
  let resolveJob;
  let rejectJob;
  let settled = false;

  const jobPromise = new Promise((resolve, reject) => {
    resolveJob = resolve;
    rejectJob = reject;
  });

  const resolveOnce = (value) => {
    if (settled) return;
    settled = true;
    resolveJob(value);
  };

  const rejectOnce = (error) => {
    if (settled) return;
    settled = true;
    rejectJob(error instanceof Error ? error : new Error(String(error)));
  };

  // Clean up: close the WebSocket when the job settles (success or failure).
  const closeWs = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.close(1000);
      } catch (_) {}
    }
  };

  // ─── Open WebSocket connection ───────────────────────────────────────────
  ws = new WebSocket(wsUrl);

  // IMPORTANT: DO NOT START apiFetch ONLY IN "open" — SEE MODULE HEADER / WEBSOCKET_PROGRESS_PARALLEL_HTTP.md

  // Ping when the socket is ready (backend associates this connection with the user).
  ws.addEventListener("open", () => {
    try {
      ws.send(JSON.stringify({ type: "ping" }));
    } catch (_) {}
  });

  // ─── On WebSocket message: progress, done, or error ────────────────────────
  // Register before HTTP starts so very fast WS messages are not dropped.
  // Backend sends JSON: event "progress" (pct + message), "done"/"complete", or stage "error".
  ws.addEventListener("message", (e) => {
    try {
      const msg = JSON.parse(e.data);

      if (msg?.stage === "error") {
        rejectOnce(msg?.message ?? "Process interrupted");
        return;
      }

      if (msg?.event === "connected" || msg?.event === "progress") {
        const pct = typeof msg?.pct === "number" ? msg.pct : 0;
        const message = msg?.message ?? "";
        const stage = msg?.stage ?? "";
        if (typeof onProgress === "function") {
          onProgress(pct, message, stage);
        }
      } else if (msg?.event === "done" || msg?.event === "complete") {
        if (typeof onProgress === "function") onProgress(100, "Completed", "done");
        // Do not resolve here: backend sends only { total_count } in WS "done", not the full
        // response (e.g. items). We resolve only from the HTTP response so the caller
        // gets the complete body (items, download_url, etc.).
      }
    } catch (_) {
      // Ignore non-JSON or parse errors
    }
  });

  // ─── On WebSocket error or close ───────────────────────────────────────────
  // FIREFOX / RELIABILITY: DO NOT reject on WS `error`. HTTP runs in parallel;
  // killing the whole job on a transient socket error caused worse UX than
  // missing progress bars. Settlement is driven by HTTP success/failure.
  ws.addEventListener("error", () => {
    /* progress-only socket; HTTP outcome drives settlement */
  });

  ws.addEventListener("close", () => {
    if (!settled) {
      rejectOnce(new Error("WebSocket closed before request completed"));
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PARALLEL HTTP — REQUIRED FOR FIREFOX AND FAIR CROSS-BROWSER TIMING
  // OLD BUG: apiFetch ONLY ran after WS "open" → REST looked "late" in Firefox.
  // FIX: run HTTP NOW; WS connects in parallel; ping on "open" still runs.
  // ═══════════════════════════════════════════════════════════════════════════
  const runHttp = async () => {
    try {
      const response = await apiFetch(request.url, request.options);

      if (!response.ok) {
        const text = await response.text();
        rejectOnce(new Error(text || `Request failed: ${response.status}`));
        return;
      }

      const result = await response.json();
      resolveOnce(result);
    } catch (err) {
      rejectOnce(err);
    }
  };

  void runHttp();

  return jobPromise.finally(closeWs);
}

// ─── Legacy export (kept for existing callers) ────────────────────────────────

/**
 * @deprecated Use requestWithProgress() for new code.
 * Same behavior: runs request with WebSocket progress; resolves with HTTP response body.
 */
export const startLiveProgressListener = requestWithProgress;
